import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogoIcon } from '@/components/Logo';

/**
 * WhyIBuiltThisPage - Founder story page
 * 
 * Design: Clean, minimal, calm, human
 * Not corporate - feels like a personal letter
 */
const WhyIBuiltThisPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Simple header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#52525B] hover:text-[#27272A] transition-colors text-sm"
          >
            <LogoIcon className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Link 
            to="/app" 
            className="text-sm text-[#0CBCBF] hover:text-[#0AA8B3] transition-colors font-medium"
          >
            Open App
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-24 pb-20 px-6">
        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          {/* Title */}
          <header className="mb-12 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#27272A] mb-4 tracking-tight">
              Why I Built This
            </h1>
            <p className="text-lg text-[#71717A] font-medium">
              Professional Scatterbrain, Lifelong Do-er
            </p>
          </header>

          {/* Story content */}
          <div className="prose-custom space-y-5 text-[#3F3F46] leading-relaxed text-[1.0625rem]">
            
            <p>
              I grew up in California, where kids didn't specialize. We just went.
            </p>
            
            <p>
              Martial arts, football, basketball, baseball, tennis, golf… if it involved running, sweating, or competing, I was in. I wasn't a master of any of it, but I was good at all of it. Which is basically the origin story of my scatterbrain: high energy, low linearity.
            </p>

            <p>
              School was the same. I read constantly, loved movies, learned from everywhere, but my attention was like a radio with a loose dial, always picking up three stations at once. I got things done. Just never in a straight line.
            </p>

            <p className="text-[#52525B]">
              College didn't change much.
            </p>

            <p>
              Rowing at sunrise, basketball in the afternoon, classes somewhere in between, parties at night. I graduated, but let's just say my GPA and my social life weren't exactly aligned.
            </p>

            <p className="text-[#52525B] font-medium">
              Then adulthood hit, and my scatterbrain really started freelancing.
            </p>

            <p>
              I wrote. Built things. Raised kids. Coached.<br />
              Answered emails between workouts, school drop-offs, coffee runs and whatever else life threw at me.
            </p>

            <p>
              I was everywhere, all the time, doing everything… just not always on time.
            </p>

            <p>
              My wife calls it dillydallying.<br />
              Doctors might call it ADD.<br />
              I call it <em>multifaceted chaos with a decent work ethic</em>.
            </p>

            <p className="text-[#52525B] font-medium">
              And somehow, it worked.
            </p>

            <p>
              I built something with my wife that lasted over two decades.<br />
              Worked as a writer long enough to see my name roll by on things people have actually watched.<br />
              Managed to stay just healthy enough to get through my yearly checkups without too many side-eyes from the doctor.
            </p>

            <p>
              One of my kids stuck with it long enough to play quarterback at a Division I university, then "served" a couple years in the NFL before starting a career in television.
            </p>

            <p>
              Another became the kind of student, athlete and teammate you can rely on. Probably play some college tennis, become an engineer and, knowing this kid, never giving up until making the world a prettier, better place.
            </p>

            <p>
              Both of them are better at grinding on one thing at a time than I am.
            </p>

            <p className="text-[#52525B] font-medium">
              Back to me…
            </p>

            <p>
              The things I built kept growing. The work got done. Sure, I missed a few dates along the way, but I showed up when it counted.
            </p>

            <p className="text-[#52525B]">
              Eventually, the scatter caught up to me. The juggling got heavier.
            </p>

            <p>
              "I'll remember that later" stopped working.
            </p>

            <p className="font-medium text-[#27272A]">
              So I admitted what everyone else already knew:
            </p>

            <p>
              I needed a system.<br />
              I needed a way back to where I was.
            </p>

            <p className="text-[#52525B] font-medium">
              So I built one.
            </p>

            <p>
              Now I'm learning to work with my scatterbrain instead of against it. Using a little structure to catch the good and filter out the noise.
            </p>

            <p className="text-[#71717A]">
              If I'd had this earlier, who knows.
            </p>

            <p className="font-medium text-[#27272A]">
              But I've got it now.
            </p>

            <p>
              I'm still scattered.<br />
              Still doing a lot.<br />
              Still me.
            </p>

            <p className="text-[#52525B] font-medium">
              Just a little more focused this time.
            </p>

            {/* Signature */}
            <p className="pt-6 text-[#27272A] font-medium">
              Brad.
            </p>

          </div>

          {/* Subtle CTA */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-16 text-center"
          >
            <p className="text-[#A1A1AA] text-sm italic">
              When you're ready, you'll know where to find your thread.
            </p>
            
            <Link 
              to="/app"
              className="inline-block mt-8 px-6 py-3 bg-[#F6FAFA] hover:bg-[#E8F7F7] text-[#0CBCBF] rounded-full text-sm font-medium transition-colors"
            >
              Find my thread
            </Link>
          </motion.div>

        </motion.article>
      </main>

      {/* Simple footer */}
      <footer className="py-8 border-t border-stone-100">
        <div className="max-w-2xl mx-auto px-6 flex justify-center gap-8 text-sm text-[#A1A1AA]">
          <Link to="/" className="hover:text-[#52525B] transition-colors">Home</Link>
          <Link to="/about" className="hover:text-[#52525B] transition-colors">About</Link>
          <Link to="/privacy" className="hover:text-[#52525B] transition-colors">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};

export default WhyIBuiltThisPage;
